'use client';

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { API_ENDPOINTS, getAuthHeaders, apiRequest } from '@/lib/api';
import { useProject } from '@/contexts/ProjectContext';

interface SprintModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode?: 'create' | 'edit';
  sprintId?: number;
  onSprintSaved?: () => void;
}

interface SprintTask {
  id: string;
  title: string;
  type: 'task' | 'bug' | 'feature';
  points: number;
  assignee: string;
  status: 'not_started' | 'in_progress' | 'testing' | 'done';
}

type StatusColor = 'slate' | 'blue' | 'emerald';

interface SprintStatus {
  id: string;
  label: string;
  color: StatusColor;
}

export default function SprintModal({ isOpen, onClose, mode = 'create', sprintId, onSprintSaved }: SprintModalProps) {
  const { selectedProject } = useProject();
  const [status, setStatus] = useState('active');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [sprintName, setSprintName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sprintGoal, setSprintGoal] = useState('');
  const [capacity, setCapacity] = useState('');
  const [hoursWorked, setHoursWorked] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState('');

  // Mock sprint tasks - will be replaced with real data later
  const [sprintTasks] = useState<SprintTask[]>([
    { id: 'GEN-247', title: 'Implement user authentication API', type: 'task', points: 5, assignee: 'Rajiv', status: 'in_progress' },
    { id: 'GEN-248', title: 'Fix login timeout bug', type: 'bug', points: 2, assignee: 'Sarah', status: 'testing' },
    { id: 'GEN-249', title: 'User dashboard feature', type: 'feature', points: 8, assignee: 'Mike', status: 'not_started' },
    { id: 'GEN-250', title: 'Database schema updates', type: 'task', points: 3, assignee: 'Rajiv', status: 'done' },
    { id: 'GEN-251', title: 'API rate limiting', type: 'task', points: 5, assignee: 'Sarah', status: 'in_progress' },
  ]);

  const statuses: SprintStatus[] = [
    { id: 'planning', label: 'Planning', color: 'slate' },
    { id: 'active', label: 'Active', color: 'emerald' },
    { id: 'completed', label: 'Completed', color: 'blue' },
  ];

  const statusColors: Record<StatusColor, string> = {
    slate: 'bg-slate-500',
    blue: 'bg-blue-500',
    emerald: 'bg-emerald-500',
  };

  const statusBgColors: Record<StatusColor, string> = {
    slate: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
    blue: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
    emerald: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  };

  const currentStatus = statuses.find(s => s.id === status);

  const taskStatusColors = {
    not_started: 'bg-slate-500',
    in_progress: 'bg-blue-500',
    testing: 'bg-purple-500',
    done: 'bg-emerald-500',
  };

  const typeIcons = {
    task: { icon: '◉', color: 'text-amber-400' },
    bug: { icon: '⚠', color: 'text-red-400' },
    feature: { icon: '★', color: 'text-blue-400' },
  };

  const totalPoints = sprintTasks.reduce((sum, task) => sum + task.points, 0);
  const completedPoints = sprintTasks.filter(t => t.status === 'done').reduce((sum, task) => sum + task.points, 0);
  const progressPercent = totalPoints > 0 ? (completedPoints / totalPoints) * 100 : 0;

  const tasksByStatus = {
    not_started: sprintTasks.filter(t => t.status === 'not_started').length,
    in_progress: sprintTasks.filter(t => t.status === 'in_progress').length,
    testing: sprintTasks.filter(t => t.status === 'testing').length,
    done: sprintTasks.filter(t => t.status === 'done').length,
  };

  const getAvatarGradient = (name: string) => {
    const gradients: { [key: string]: string } = {
      'Rajiv': 'from-cyan-400 to-blue-500',
      'Sarah': 'from-emerald-400 to-teal-500',
      'Mike': 'from-purple-400 to-pink-500',
    };
    return gradients[name] || 'from-slate-400 to-slate-500';
  };

  const fetchSprintData = async () => {
    if (!sprintId) return;
    
    try {
      setFetching(true);
      setError('');
      const response = await apiRequest(`${API_ENDPOINTS.SPRINTS}${sprintId}/`);

      if (response.ok) {
        const sprint = await response.json();
        setSprintName(sprint.name || '');
        setStartDate(sprint.start_date || '');
        setEndDate(sprint.end_date || '');
        setCapacity(sprint.total_possible_hours?.toString() || '');
        setSprintGoal(''); // Not stored in backend model
        setHoursWorked(''); // Calculated from tasks, not stored
        
        // Determine status from dates
        if (sprint.start_date && sprint.end_date) {
          const today = new Date();
          const start = new Date(sprint.start_date);
          const end = new Date(sprint.end_date);
          if (today < start) {
            setStatus('planning');
          } else if (today > end) {
            setStatus('completed');
          } else {
            setStatus('active');
          }
        }
      } else {
        setError('Failed to load sprint data');
      }
    } catch (error) {
      setError('Unable to load sprint data');
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && sprintId) {
        fetchSprintData();
      } else {
        // Reset form for create mode
        setSprintName('');
        setStartDate('');
        setEndDate('');
        setSprintGoal('');
        setCapacity('');
        setHoursWorked('');
        setStatus('active');
        setError('');
      }
    }
  }, [isOpen, mode, sprintId]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  const handleSave = async () => {
    if (!selectedProject) {
      setError('Please select a project first');
      return;
    }

    if (!sprintName.trim()) {
      setError('Sprint name is required');
      return;
    }

    if (!startDate) {
      setError('Start date is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const url = mode === 'edit' && sprintId
        ? `${API_ENDPOINTS.SPRINTS}${sprintId}/`
        : `${API_ENDPOINTS.SPRINTS}?project=${selectedProject.id}`;
      
      const method = mode === 'edit' ? 'PATCH' : 'POST';

      const requestBody: Record<string, unknown> = {
        name: sprintName,
        start_date: startDate || null,
        end_date: endDate || null,
        total_possible_hours: capacity ? parseFloat(capacity) : null,
      };

      const response = await apiRequest(url, {
        method,
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        await response.json();
        if (onSprintSaved) {
          onSprintSaved();
        }
        onClose();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || errorData.message || `Failed to ${mode === 'edit' ? 'update' : 'create'} sprint`);
      }
    } catch (err) {
      setError(`Unable to ${mode === 'edit' ? 'update' : 'create'} sprint`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!sprintId) return;
    
    if (!confirm('Are you sure you want to delete this sprint?')) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await apiRequest(`${API_ENDPOINTS.SPRINTS}${sprintId}/`, {
        method: 'DELETE',
      });

      if (response.ok || response.status === 204) {
        if (onSprintSaved) {
          onSprintSaved();
        }
        onClose();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || errorData.message || 'Failed to delete sprint');
      }
    } catch (err) {
      setError('Unable to delete sprint');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <>
      {/* Modal Backdrop */}
      <div 
        className="fixed inset-0 bg-gray-300/50 backdrop-blur-sm z-[100]"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[101] flex items-center justify-center p-4 pointer-events-none">
        <div 
          className="relative w-full max-w-2xl bg-[#0f1429] rounded-xl border border-[#1a1f3a] shadow-2xl overflow-hidden pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#1a1f3a] bg-[#1a1f3a]/50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Sprint Details</h2>
                <p className="text-xs text-gray-500">Manage sprint configuration and view progress</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-300 p-1.5 hover:bg-[#2d1b3d] rounded transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Modal Body */}
          <div className="p-6 max-h-[70vh] overflow-y-auto">
            {error && (
              <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
                {error}
              </div>
            )}
            
            {fetching && (
              <div className="mb-4 text-gray-400 text-sm">Loading sprint data...</div>
            )}
            {/* Sprint Name */}
            <div className="mb-5">
              <label className="block text-xs text-gray-400 mb-1.5">Sprint Name</label>
              <input
                type="text"
                value={sprintName}
                onChange={(e) => setSprintName(e.target.value)}
                placeholder="Enter sprint name"
                className="w-full bg-[#1a1f3a] border border-[#2d1b3d] rounded-lg px-4 py-3 text-lg font-semibold text-white placeholder-gray-500 focus:outline-none focus:border-amber-500/50"
              />
            </div>

            {/* Status & Dates Row */}
            <div className="grid grid-cols-3 gap-4 mb-5">
              {/* Status */}
              <div className="relative">
                <label className="block text-xs text-gray-400 mb-1.5">Status</label>
                <button
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${statusBgColors[currentStatus?.color || 'slate']}`}
                >
                  <span className={`w-2 h-2 rounded-full ${statusColors[currentStatus?.color || 'slate']}`}></span>
                  {currentStatus?.label}
                  <svg className="w-4 h-4 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {showStatusDropdown && (
                  <>
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setShowStatusDropdown(false)}
                    />
                    <div className="absolute top-full left-0 right-0 mt-1 bg-[#1a1f3a] border border-[#2d1b3d] rounded-lg shadow-xl z-20 py-1">
                      {statuses.map((s) => (
                        <button
                          key={s.id}
                          onClick={() => { setStatus(s.id); setShowStatusDropdown(false); }}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-[#2d1b3d] transition-colors ${
                            status === s.id ? 'bg-[#2d1b3d]/50' : ''
                          }`}
                        >
                          <span className={`w-2 h-2 rounded-full ${statusColors[s.color]}`}></span>
                          <span className="text-gray-300">{s.label}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Start Date */}
              <div>
                <label className="block text-xs text-gray-400 mb-1.5">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full bg-[#1a1f3a] border border-[#2d1b3d] rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-amber-500/50"
                />
              </div>

              {/* End Date */}
              <div>
                <label className="block text-xs text-gray-400 mb-1.5">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full bg-[#1a1f3a] border border-[#2d1b3d] rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-amber-500/50"
                />
              </div>
            </div>

            {/* Goal */}
            <div className="mb-5">
              <label className="block text-xs text-gray-400 mb-1.5">Sprint Goal</label>
              <textarea
                rows={2}
                value={sprintGoal}
                onChange={(e) => setSprintGoal(e.target.value)}
                placeholder="What is the main objective for this sprint?"
                className="w-full bg-[#1a1f3a] border border-[#2d1b3d] rounded-lg px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-500/50 resize-none"
              />
            </div>

            {/* Capacity */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-xs text-gray-400 mb-1.5">Capacity (hours)</label>
                <input
                  type="number"
                  value={capacity}
                  onChange={(e) => setCapacity(e.target.value)}
                  placeholder="0"
                  className="w-full bg-[#1a1f3a] border border-[#2d1b3d] rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:border-amber-500/50"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1.5">Hours Worked</label>
                <input
                  type="number"
                  value={hoursWorked}
                  onChange={(e) => setHoursWorked(e.target.value)}
                  placeholder="0"
                  className="w-full bg-[#1a1f3a] border border-[#2d1b3d] rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:border-amber-500/50"
                />
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-[#1a1f3a] my-6"></div>

            {/* Sprint Stats */}
            <div className="mb-6">
              <label className="block text-xs text-gray-400 mb-3">Sprint Progress</label>
              
              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex justify-between text-xs mb-1.5">
                  <span className="text-gray-400">Story Points</span>
                  <span className="text-gray-300">{completedPoints} / {totalPoints} pts ({Math.round(progressPercent)}%)</span>
                </div>
                <div className="w-full bg-[#1a1f3a] rounded-full h-2.5">
                  <div 
                    className="bg-gradient-to-r from-amber-500 to-orange-500 h-2.5 rounded-full transition-all"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-[#1a1f3a]/50 rounded-lg p-3 border border-[#2d1b3d]">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-2 h-2 rounded-full bg-slate-500"></span>
                    <span className="text-xs text-gray-400">Not Started</span>
                  </div>
                  <div className="text-2xl font-semibold text-white">{tasksByStatus.not_started}</div>
                </div>
                <div className="bg-[#1a1f3a]/50 rounded-lg p-3 border border-[#2d1b3d]">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                    <span className="text-xs text-gray-400">In Progress</span>
                  </div>
                  <div className="text-2xl font-semibold text-white">{tasksByStatus.in_progress}</div>
                </div>
                <div className="bg-[#1a1f3a]/50 rounded-lg p-3 border border-[#2d1b3d]">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                    <span className="text-xs text-gray-400">Testing</span>
                  </div>
                  <div className="text-2xl font-semibold text-white">{tasksByStatus.testing}</div>
                </div>
                <div className="bg-[#1a1f3a]/50 rounded-lg p-3 border border-[#2d1b3d]">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                    <span className="text-xs text-gray-400">Done</span>
                  </div>
                  <div className="text-2xl font-semibold text-white">{tasksByStatus.done}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-[#1a1f3a] bg-[#1a1f3a]/50">
            {mode === 'edit' && (
              <button 
                onClick={handleDelete}
                className="flex items-center gap-2 text-red-400 hover:text-red-300 text-sm transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete Sprint
              </button>
            )}
            {mode === 'create' && <div />}
            <div className="flex items-center gap-3 ml-auto">
              <button 
                onClick={onClose}
                className="px-4 py-2 text-gray-400 hover:text-gray-300 text-sm transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={handleSave}
                disabled={loading}
                className="px-5 py-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  // Use portal to render modal at document body level
  if (typeof window !== 'undefined') {
    return createPortal(modalContent, document.body);
  }
  
  return null;
}

